import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { KnowledgeApi } from "~/functions/api/knowledge";
import type { RootState } from "~/store";

interface UsePendingKnowledgeCountReturn {
  count: number;
  loading: boolean;
  error: string | null;
  refetch: () => void;
  decrementCount: () => void;
}

let globalRefetchCallbacks: (() => void)[] = [];

export const usePendingKnowledgeCount = (): UsePendingKnowledgeCountReturn => {
  const [count, setCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const { currentUser, currentProject } = useSelector((state: RootState) => state.auth) as any;
  const knowledgeApi = useMemo(() => new KnowledgeApi(), []);

  const fetchPendingKnowledgeCount = useCallback(async () => {
    if (!currentProject?.id || !currentUser?.organization_id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const pendingData = await knowledgeApi.getByStatus(
        currentProject.id,
        currentUser.organization_id,
        "pending",
      );
      const pendingArray = pendingData
        ? Array.isArray(pendingData)
          ? pendingData
          : [pendingData]
        : [];
      setCount(pendingArray.length);
    } catch (error) {
      console.error("Failed to fetch pending knowledge count:", error);
      setError("承認待ちナレッジ数の取得に失敗しました");
      setCount(0);
    } finally {
      setLoading(false);
    }
  }, [currentProject?.id, currentUser?.organization_id, knowledgeApi]);

  useEffect(() => {
    fetchPendingKnowledgeCount();

    // グローバルコールバックに登録
    globalRefetchCallbacks.push(fetchPendingKnowledgeCount);

    // クリーンアップ時に登録解除
    return () => {
      globalRefetchCallbacks = globalRefetchCallbacks.filter(
        (cb) => cb !== fetchPendingKnowledgeCount,
      );
    };
  }, [fetchPendingKnowledgeCount]);

  const decrementCount = useCallback(() => {
    setCount((prev) => Math.max(0, prev - 1));
  }, []);

  const refetch = useCallback(() => {
    fetchPendingKnowledgeCount();
  }, [fetchPendingKnowledgeCount]);

  return {
    count,
    loading,
    error,
    refetch,
    decrementCount,
  };
};

// 全てのフックインスタンスで再フェッチをトリガーする関数
export const refetchAllPendingKnowledgeCounts = () => {
  globalRefetchCallbacks.forEach((callback) => callback());
};
